import React, { FC, useState } from "react";
import { Text } from "@smartsuite/react-ui/lib";
import { DangerAlert } from "@smartsuite/react-ui/lib/components/info/DangerAlert/DangerAlert";
import { IconButton } from "@smartsuite/react-ui/lib/components/buttons/IconButton/IconButton";
import { Tooltip } from "@smartsuite/react-ui/lib/components/help/Tooltip";
import { Colors } from "@smartsuite/types";
import { Document, Page, pdfjs } from "react-pdf";

import { FieldVisibilityCallback } from "../../../hooks/useFormFieldVisibility";

import { FormConfigItem } from "../../../types/form";

import { useFileStackContext } from "../../../hooks/useFileStackContext";
import { FormFieldControl } from "../../FormFieldControl/FormFieldControl";
import { formPdfViewerItemText } from "./FormPdfViewerItem.text";
import { $t } from "../../../utils/intl";
import { addContentDisposition } from "../../../utils/filestack";

import "./FormPdfViewerItem.sass";

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/4.8.69/pdf.worker.mjs`;

interface FormPdfViewerItemProps {
  item: FormConfigItem;
  checkFieldVisibility: FieldVisibilityCallback;
}

export const FormPdfViewerItem: FC<FormPdfViewerItemProps> = ({ item, checkFieldVisibility }) => {
  const [numPages, setNumPages] = useState<number>();
  const [pageNumber, setPageNumber] = useState(1);

  const { getFileStackUrl } = useFileStackContext();

  const isItemConditionSatisfied = checkFieldVisibility(item.slug);

  const pdfUrl =
    item.params.type === "upload" ? getFileStackUrl(item.params.handle) : item.params.url;

  if (!isItemConditionSatisfied || !pdfUrl) return null;

  const onDocumentLoadSuccess = ({ numPages: totalPages }: { numPages: number }): void => {
    setNumPages(totalPages);
  };

  const handleDowndload = (): void => {
    if (item.params.type === "upload" && item.params.handle && item.params.metadata?.filename) {
      window.open(addContentDisposition(pdfUrl, item.params.metadata?.filename));
    }
    if (item.params.type === "link" && item.params.url) {
      window.open(pdfUrl, "_blank");
    }
  };

  const isPrevDisabled = !numPages || pageNumber === 1;
  const isNextDisabled = !numPages || pageNumber === numPages;

  return (
    <FormFieldControl
      captionAsTooltip={item.params.help_text_as_tooltip}
      caption={item.params.caption}
      label={item.params.label}
      helpText={item.params.helpText}
      required={item.required}
    >
      <div className="form-pdf-viewer-item" data-field-name={item.slug}>
        <div className="form-pdf-viewer-item__nav">
          <div className="form-pdf-viewer-item__nav-actions">
            <div className="form-pdf-viewer-item__nav-arrows">
              <Tooltip
                padding={2}
                placements={["top"]}
                target={
                  <IconButton
                    disabled={isPrevDisabled}
                    className="form-pdf-viewer-item__arrow form-pdf-viewer-item__icon-btn rotate-90"
                    name="long-arrow-down"
                    ariaLabel={$t(formPdfViewerItemText.prevPage)}
                    size="S"
                    color={Colors.custom16}
                    onClick={() => setPageNumber(pageNumber - 1)}
                  />
                }
                text={$t(formPdfViewerItemText.prevPage)}
              />
              <Tooltip
                padding={2}
                placements={["top"]}
                target={
                  <IconButton
                    disabled={isNextDisabled}
                    className="form-pdf-viewer-item__arrow form-pdf-viewer-item__icon-btn -rotate-90"
                    name="long-arrow-down"
                    ariaLabel={$t(formPdfViewerItemText.nextPage)}
                    size="S"
                    color={Colors.custom16}
                    onClick={() => setPageNumber(pageNumber + 1)}
                  />
                }
                text={$t(formPdfViewerItemText.nextPage)}
              />
            </div>
            <div className="form-pdf-viewer-item__nav-arrows-divider" />
            <Tooltip
              padding={2}
              placements={["top"]}
              target={
                <IconButton
                  disabled={!numPages}
                  className="form-pdf-viewer-item__download form-pdf-viewer-item__icon-btn"
                  name="download-thin"
                  ariaLabel={$t(formPdfViewerItemText.download)}
                  size="S"
                  color={Colors.custom16}
                  onClick={handleDowndload}
                />
              }
              text={$t(formPdfViewerItemText.download)}
            />
          </div>
          {numPages && (
            <Text size="s" color="secondary">
              {$t(formPdfViewerItemText.pageNumber, {
                pageNumber,
                totalPages: numPages,
              })}
            </Text>
          )}
        </div>
        <div
          className="form-pdf-viewer-item__container"
          style={{ maxHeight: item.params.maxHeight, minHeight: item.params.maxHeight }}
        >
          <Document
            file={pdfUrl}
            onLoadSuccess={onDocumentLoadSuccess}
            loading={null}
            error={
              <DangerAlert className="form-pdf-viewer-item__error">
                <Text size="m" color="primary">
                  {$t(formPdfViewerItemText.error)}
                </Text>
              </DangerAlert>
            }
          >
            <Page
              className="form-pdf-viewer-item__page"
              loading={null}
              renderTextLayer={false}
              renderAnnotationLayer={false}
              pageNumber={pageNumber}
            />
          </Document>
        </div>
      </div>
    </FormFieldControl>
  );
};
